
import React, { useCallback, useMemo, useRef, useState } from 'react';
import "../../../center admin css/centerAdmin.css"


export type ImageUploadBoxProps = {
  className?: string;
  label?: string;
  /** Accept as string or array (e.g., ".pdf,.png" or ['.pdf','.png']) */
  accept?: string | string[];
  /** Allow multiple selection */
  multiple?: boolean;
  /** Max file size per file, in MB */
  maxSizeMB?: number;

  /** New preferred callback: all selected files */
  onFilesSelected?: (files: File[]) => void;

  /** Back-compat aliases (so old screens don’t break) */
  onUpload?: (file: File) => void;            // single-file
  onFileSelect?: (files: File[]) => void;     // plural
  onChange?: (files: File[]) => void;         // plural

  /** Custom inner UI (dropzone content) */
  placeholder?: React.ReactNode;
};

const ImageUploadBox: React.FC<ImageUploadBoxProps> = ({
  className,
  accept,
  multiple = false,
  maxSizeMB,
  onFilesSelected,
  onUpload,
  onFileSelect,
  onChange,
  placeholder,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const acceptAttr = useMemo(() => {
    if (!accept) return undefined;
    return Array.isArray(accept) ? accept.join(',') : accept;
  }, [accept]);

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  const toArray = (list: FileList | null): File[] => (list ? Array.from(list) : []);

  const filterBySize = (files: File[]): File[] => {
    if (!maxSizeMB) return files;
    const limit = maxSizeMB * 1024 * 1024;
    const ok: File[] = [];
    for (const f of files) {
      if (f.size <= limit) ok.push(f);
      else console.warn(`File "${f.name}" skipped: > ${maxSizeMB} MB`);
    }
    return ok;
  };

  const emit = (files: File[]) => {
    const filtered = filterBySize(files);
    if (!filtered.length) return;
    onFilesSelected?.(filtered);
    onFileSelect?.(filtered);
    onChange?.(filtered);
    if (onUpload) onUpload(filtered[0]); // single-file legacy
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emit(toArray(e.target.files));
    e.currentTarget.value = ''; // allow re-selecting same file
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const files = toArray(e.dataTransfer.files);
    emit(multiple ? files : files.slice(0, 1));
  };

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      onClick={openPicker}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openPicker()}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-label="Upload file"
      style={{
        outline: dragOver ? '2px solid rgba(23,67,80,0.35)' : 'none',
        outlineOffset: dragOver ? '2px' : '0',
        cursor: 'pointer',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        multiple={multiple}
        onChange={onInputChange}
        style={{ display: 'none' }}
      />
      {placeholder ?? (
        <div style={{ textAlign: 'center', padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Drag and drop files here, or click to browse
          </div>
          {maxSizeMB ? (
            <div style={{ opacity: 0.7, fontSize: 12 }}>Max file size: {maxSizeMB} MB</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ImageUploadBox;

